import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ErrorPage from 'next/error';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getPosts, getCategoriess } from '../../../utils/api/wordpress-api';
import ArticlesSubHeader from '../../../components/Articles/SubHeader';
import ArticlesMain from '../../../components/Articles/Main';
import BlogSEO from '../../../components/SEO/ForBlog';
import { checkLocaleAllowed } from '../../blog';

interface Props {
  posts: any;
  categories: any[];
  pageContext: any;
  page: number;
}

function Index({ posts: { edges }, categories, pageContext, page }: Props) {
  const { t } = useTranslation('blog');
  const router = useRouter();
  const { locale } = router;
  if (!checkLocaleAllowed(locale)) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      <BlogSEO title={t('title.page', { pageNum: page })} />
      <ArticlesSubHeader categories={categories} />
      <ArticlesMain posts={edges} pageContext={pageContext} locale={locale} />
    </>
  );
}

const fetchPagesPromise = async (locale: string) => {
  try {
    // Only pre-generate the first 5 pages per locale at build time.
    // Later pages are generated on-demand via fallback: 'blocking'.
    const maxPreGeneratedPages = 5;
    const pageArr = Array.from(
      Array(maxPreGeneratedPages).keys(),
      (num) => num + 1,
    );
    return pageArr.map((pageNum: number) => ({
      params: {
        page: pageNum.toString(),
      },
      locale,
    }));
  } catch (error) {
    console.warn(`Failed to fetch blog pages for locale ${locale}:`, error);
    return [];
  }
};

export const getStaticPaths = async () => {
  const locales = ['ja', 'en', 'es', 'pt'];
  const result = await Promise.all(
    locales.map((locale: string) => fetchPagesPromise(locale)),
  );
  const paths = result.flat();
  return {
    paths,
    fallback: 'blocking',
  };
};

export async function getStaticProps({ preview = false, params, locale }) {
  try {
    const { page } = params;
    const offset = (page - 1) * 10;
    const posts = await getPosts(
      {
        offset,
      },
      locale,
    );
    if (!posts?.edges) {
      return { notFound: true, revalidate: 60 };
    }
    const categoriesData = await getCategoriess(locale);
    const basePath = '/blog';
    const baseAsPath = `/blog`;
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common', 'blog'])),
        page,
        posts,
        preview,
        categories: categoriesData?.categories || [],
        pageContext: {
          totalCount: posts.pageInfo?.offsetPagination?.total || 0,
          limit: 10,
          skip: offset,
          filter: null,
          searchTerm: null,
          basePath,
          baseAsPath,
        },
      },
      revalidate: 3600, // Regenerate page every hour
    };
  } catch (error) {
    console.error(`Failed to fetch blog page ${params.page}:`, error);
    return { notFound: true, revalidate: 60 };
  }
}

export default Index;
